import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";

import type { ViewerReturn } from "@constructionautomation/backend/convex/auth";

const SENSITIVE_KEYS = [
  "authorization",
  "cookie",
  "token",
  "secret",
  "password",
  "apiKey",
  "api_key",
  "pdfText",
  "documentContent",
  "llmPrompt",
  "llmResponse",
  "prompt",
  "completion",
];

const SAFE_SENTRY_CONTEXT_TAGS = new Set(["orgId", "projectId", "jobId"]);

export interface SentryUserContextProps {
  viewer: ViewerReturn;
}

function redactSensitiveData(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (depth > 8) return "[Filtered]";
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveData(item, depth + 1, seen));
  }
  if (value && typeof value === "object") {
    if (seen.has(value)) return "[Filtered]";
    seen.add(value);
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        isSensitiveKey(key) ? "[Filtered]" : redactSensitiveData(entry, depth + 1, seen),
      ])
    );
  }
  return value;
}

function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitiveKey) => lowerKey.includes(sensitiveKey.toLowerCase()));
}

function getRouteTags(pathname: string): Record<string, string> {
  const tags: Record<string, string> = {};
  const jobMatch = pathname.match(/^\/(?:specPilot|takeoffPilot)\/([^/]+)/);
  const projectMatch = pathname.match(/^\/project(?:s)?\/([^/]+)/);
  if (jobMatch?.[1]) tags.jobId = jobMatch[1];
  if (projectMatch?.[1]) tags.projectId = projectMatch[1];
  return tags;
}

/**
 * Initializes browser Sentry for route tracing and error capture.
 *
 * @param router - TanStack Router instance used by Sentry's browser tracing integration.
 * @returns void
 * @remarks Reads Sentry settings from Vite env vars and redacts sensitive event fields in beforeSend.
 */
export function initSentry(router: AnyRouter): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? import.meta.env.VITE_RELEASE,
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0"),
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
        response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      },
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
    },
    beforeSend: (event) => {
      event.extra = redactSensitiveData(event.extra) as typeof event.extra;
      event.contexts = redactSensitiveData(event.contexts) as typeof event.contexts;
      event.request = redactSensitiveData(event.request) as typeof event.request;
      event.breadcrumbs = redactSensitiveData(event.breadcrumbs) as typeof event.breadcrumbs;
      event.user = redactSensitiveData(event.user) as typeof event.user;
      event.tags = {
        ...event.tags,
        ...getRouteTags(window.location.pathname),
      };
      return event;
    },
  });
}

/**
 * Binds authenticated viewer context to Sentry without sending user identifiers.
 *
 * @param props - Component props.
 * @param props.viewer - Current authenticated viewer used to set the organization tag.
 * @returns null
 * @remarks Updates Sentry tags when viewer changes and clears user context when viewer is absent.
 */
export function SentryUserContext({ viewer }: SentryUserContextProps) {
  useEffect(() => {
    if (!viewer) {
      Sentry.setUser(null);
      Sentry.setTag("orgId", "none");
      return;
    }
    Sentry.setUser(null);
    Sentry.setTag("orgId", viewer.organizationId ?? "none");
  }, [viewer]);

  return null;
}

/**
 * Captures a user-action exception with a scoped action tag and safe context tags.
 *
 * @param error - Unknown exception or rejection reason to report.
 * @param action - Stable action name, for example "auth.syncIdentity".
 * @param context - Optional context map; only SAFE_SENTRY_CONTEXT_TAGS are promoted to tags.
 * @returns void
 */
export function captureUserActionError(
  error: unknown,
  action: string,
  context: Record<string, string | null | undefined> = {}
): void {
  Sentry.withScope((scope) => {
    scope.setTag("userAction", action);
    for (const [key, value] of Object.entries(context)) {
      if (value && SAFE_SENTRY_CONTEXT_TAGS.has(key)) scope.setTag(key, value);
    }
    Sentry.captureException(error);
  });
}

export { Sentry };
